import React, { ReactNode, useRef, useEffect, useState, useCallback } from 'react';
import Portal from './Portal';

interface DropdownItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  placement?: 'bottom-left' | 'bottom-right' | 'auto';
}

interface Position {
  top: number;
  left: number;
  right?: number;
}

const Dropdown: React.FC<DropdownProps> = ({ 
  trigger, 
  items, 
  isOpen, 
  onClose, 
  className = '',
  placement = 'auto'
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  // Calculate dropdown position
  const calculatePosition = useCallback((): Position => {
    if (!triggerRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Default position below the trigger
    let top = triggerRect.bottom + window.scrollY;
    let left = triggerRect.left + window.scrollX;
    let right: number | undefined;

    // Estimate dropdown width (will be refined after render)
    const dropdownWidth = dropdownRef.current?.offsetWidth || 200;
    const dropdownHeight = dropdownRef.current?.offsetHeight || (items.length * 40 + 16);

    // Horizontal positioning
    if (placement === 'bottom-right' || (placement === 'auto' && triggerRect.left + dropdownWidth > viewportWidth)) {
      // Position to the right edge of trigger, extending left
      right = viewportWidth - triggerRect.right - window.scrollX;
      left = undefined as any;
    } else {
      // Position to the left edge of trigger, extending right
      left = triggerRect.left + window.scrollX;
    }

    // Vertical positioning - if not enough space below, position above
    if (triggerRect.bottom + dropdownHeight > viewportHeight) {
      top = triggerRect.top + window.scrollY - dropdownHeight;
    }

    // Ensure dropdown doesn't go off-screen vertically
    if (top < window.scrollY) {
      top = window.scrollY + 8;
    }

    return { top, left: left as number, right };
  }, [items.length, placement]);

  // Update position when dropdown opens or window resizes
  useEffect(() => {
    if (isOpen) {
      setPosition(calculatePosition());
    }
  }, [isOpen, calculatePosition]);

  // Handle window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      setPosition(calculatePosition());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isOpen, calculatePosition]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is outside both trigger and dropdown
      if (
        triggerRef.current &&
        dropdownRef.current &&
        !triggerRef.current.contains(target) &&
        !dropdownRef.current.contains(target)
      ) {
        onClose();
      }
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      <div ref={triggerRef} className={`dropdown-trigger ${className}`}>
        {trigger}
      </div>
      
      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className="dropdown-menu"
            style={{
              position: 'absolute',
              top: position.top,
              left: position.right !== undefined ? 'auto' : position.left,
              right: position.right,
              zIndex: 9999,
              minWidth: '12rem',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              padding: '0.5rem 0',
            }}
          >
            {items.map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick();
                    onClose();
                  }
                }}
                disabled={item.disabled}
                className={`dropdown-item ${item.className || ''}`}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1,
                  color: '#374151',
                  transition: 'background-color 0.15s ease-in-out',
                }}
                onMouseEnter={(e) => {
                  if (!item.disabled) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="flex items-center">
                  {item.icon && (
                    <span className="mr-2" style={{ width: '1rem', height: '1rem' }}>
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </div>
              </button>
            ))}
          </div>
        </Portal>
      )}
    </>
  );
};

export default Dropdown;